import React from 'react';
import { InfoCircleOutlined } from '@ant-design/icons';
import { Tag, Typography } from 'antd';
import ReactMarkdown from 'react-markdown';
import rehypeKatex from 'rehype-katex';
import remarkMath from 'remark-math';
import 'katex/dist/katex.min.css';

export type PillarThreeGuideKey =
  | 'risk-size'
  | 'common-factors'
  | 'risk-adaptation';

const RISK_SIZE = String.raw`
This component asks whether the user took the right **amount and distribution
of risk** compared with the optimal portfolio. It combines four scores.

### Adverse-market-loss budget — 30% of this component

PM applies a fixed adverse move to every position and estimates the portfolio's
loss. A result close to the optimal portfolio scores best. Taking too little
risk can lose points as well as taking too much. Loss above the portfolio's
adverse-market-loss limit receives an additional penalty.

This is a fixed, direction-neutral stress estimate. It is not historical
Value-at-Risk and it does not use the period's realized profit or loss.

### Adverse-loss allocation — 30%

For every instrument, PM calculates its share of the user's total estimated
loss and its share of the optimal portfolio's loss. It adds the absolute gaps
between those shares:

$$
\mathrm{AllocationScore}
=\max\!\left(0,\min\!\left(1,
1-0.5\sum_i|c_i-b_i|\right)\right).
$$

Matching the optimal distribution scores $100\%$. Concentrating the same total
risk in different instruments lowers the score.

### Primary-risk construction — 20%

When the scenario contains bonds, PM compares both:

- how absolute interest-rate risk is distributed across instruments; and
- the total amount of that risk.

The distribution comparison supplies $60\%$ of this sub-score and the amount
comparison supplies $40\%$. If a scenario has no bonds, this channel uses the
scenario's most-represented risk class instead, so it does not become an empty
automatic score.

### Risk-limit discipline — 20%

PM adds weighted excesses above the portfolio limits into $E$. The
adverse-market-loss breach has twice the normal weight, net bond
interest-rate-risk has $1.2$ times the normal weight, and total and bond
exposure have $0.8$ times the normal weight. Other checks have normal weight.

$$
\mathrm{DisciplineScore}=e^{-0.9E}.
$$

No breach scores $100\%$; larger or multiple breaches reduce the score
progressively.

### Component formula

$$
\mathrm{RiskSize}
=0.30\,\mathrm{LossBudget}
+0.30\,\mathrm{LossAllocation}
+0.20\,\mathrm{PrimaryRiskConstruction}
+0.20\,\mathrm{Discipline}.
$$

Risk size supplies up to $40$ Pillar 3 points.
`;

const COMMON_FACTORS = String.raw`
This component asks whether the user's **combined exposures and
concentrations** resemble the optimal portfolio. It combines three scores.

### Aggregate exposure pattern — 40% of this component

PM compares six complete-portfolio measures:

1. net bond interest-rate sensitivity;
2. gross bond interest-rate sensitivity;
3. net equity exposure;
4. gross equity exposure;
5. gross foreign-exchange exposure; and
6. same-direction US-dollar exposure.

Each measure is put on a fixed reference scale, and its gap from the optimal
portfolio is capped at $100\%$. The score is one minus the average of the six
gaps. This tests the shape of the user's shared market-factor exposures rather
than individual position-by-position agreement.

### Concentration awareness — 25%

PM compares three concentration ratios with the optimal portfolio:

- absolute net bond sensitivity divided by gross bond sensitivity;
- absolute net equity exposure divided by gross equity exposure; and
- same-direction US-dollar exposure divided by gross foreign-exchange
  exposure.

A high ratio means that nominally separate positions are mostly one
directional bet. The closer all three ratios are to the optimal portfolio, the
higher the score.

### Risk-limit discipline — 35%

The same weighted breach total, $E$, used by the Risk size component becomes:

$$
\mathrm{DisciplineScore}=e^{-0.9E}.
$$

Because discipline is $35\%$ of this component, limit breaches can materially
reduce the common-factors score even when its exposure pattern is otherwise
close to the optimal portfolio.

### Component formula

$$
\mathrm{CommonFactors}
=0.40\,\mathrm{ExposurePattern}
+0.25\,\mathrm{Concentration}
+0.35\,\mathrm{Discipline}.
$$

Aggregate common factors supply up to $35$ Pillar 3 points.
`;

const RISK_ADAPTATION = String.raw`
This component asks whether the user carried risk efficiently and **changed
the risk budget and mix as the optimal portfolio changed**. It combines four
scores.

### Risk density — 35% of this component

PM compares two risk-per-dollar ratios with the optimal portfolio:

- estimated adverse-market loss divided by total gross exposure; and
- gross bond interest-rate sensitivity divided by gross bond exposure.

The two comparisons receive $60\%$ and $40\%$ of this sub-score respectively.
Exact proportional matches score best; increasingly different ratios decay
toward zero.

### Risk-budget change — 25%

From the second period onward, PM compares the change in the user's estimated
adverse-market loss with the change in the optimal portfolio's loss. It rewards
increasing or reducing the total risk budget by a similar amount.

In the first period there is no earlier period to compare, so PM reuses the
adverse-market-loss budget score from the Risk size component.

### Risk-mix reallocation — 25%

PM tracks every instrument's share of total estimated loss. It compares how
those shares changed since the previous period with how the optimal portfolio's
shares changed. Rotating toward the same instruments scores better than
leaving the old mix unchanged.

In the first period, PM reuses the adverse-loss allocation score because there
is no earlier mix.

### Risk-limit discipline — 15%

The same weighted limit-breach score is included again:

$$
\mathrm{DisciplineScore}=e^{-0.9E}.
$$

### Component formula

$$
\mathrm{RiskAdaptation}
=0.35\,\mathrm{RiskDensity}
+0.25\,\mathrm{RiskBudgetChange}
+0.25\,\mathrm{RiskMixChange}
+0.15\,\mathrm{Discipline}.
$$

The source model stores this result under the historical name
**vol/correlation adaptation**, but it does not estimate market volatility or
correlations. It scores the risk-density and period-to-period changes described
above.

Risk adaptation supplies up to $25$ Pillar 3 points.
`;

const GUIDES: Record<
  PillarThreeGuideKey,
  {
    explanation: string;
    points: string;
    subtitle: string;
    title: string;
  }
> = {
  'risk-size': {
    explanation: RISK_SIZE,
    points: '40% of Pillar 3',
    subtitle:
      'How risk amount, distribution, primary-risk construction, and limits become up to 40 points.',
    title: 'Risk size and intended loss',
  },
  'common-factors': {
    explanation: COMMON_FACTORS,
    points: '35% of Pillar 3',
    subtitle:
      'How aggregate exposures, concentration, and limits become up to 35 points.',
    title: 'Aggregate common factors',
  },
  'risk-adaptation': {
    explanation: RISK_ADAPTATION,
    points: '25% of Pillar 3',
    subtitle:
      'How risk density and period-to-period adjustments become up to 25 points.',
    title: 'Risk adaptation',
  },
};

const PillarThreeComponentGuide: React.FC<{
  component: PillarThreeGuideKey;
}> = ({ component }) => {
  const guide = GUIDES[component];

  return (
    <aside
      aria-labelledby={`pillar-three-${component}-title`}
      className="assessment-inspect-assessment__pillar-one-guide"
    >
      <header className="assessment-inspect-assessment__pillar-one-guide-header">
        <div className="assessment-inspect-assessment__pillar-one-guide-kicker">
          <span>
            <InfoCircleOutlined aria-hidden="true" /> Scoring guide
          </span>
          <Tag color="blue">{guide.points}</Tag>
        </div>
        <Typography.Title id={`pillar-three-${component}-title`} level={4}>
          {guide.title}
        </Typography.Title>
        <Typography.Text type="secondary">{guide.subtitle}</Typography.Text>
      </header>

      <div className="assessment-inspect-assessment__pillar-one-guide-body">
        <ReactMarkdown
          rehypePlugins={[rehypeKatex]}
          remarkPlugins={[remarkMath]}
        >
          {guide.explanation}
        </ReactMarkdown>
      </div>
    </aside>
  );
};

export default PillarThreeComponentGuide;
